// ─────────────────────────────────────────────────────────────────────────────
// AIDEY DESIGN TOKENS FOR JS (UI-1).
//
// The same values as src/ui/tokens.css, for inline styles and logic. Prefer v('ink')
// (which returns 'var(--ink)') in styles, so the stylesheet stays the one place a
// value is set; read TOKENS only where a literal is unavoidable (a canvas, a chart
// library, a contrast calculation). tokens.test.js fails if this file and tokens.css
// disagree in either direction, including the phone overrides.
// ─────────────────────────────────────────────────────────────────────────────

export const TOKENS = {
  // Core colours
  'ink': '#1e1b3a',
  'ink-2': '#3f3c58',
  'ink-3': '#5a5772',
  'ink-4': '#6b6880',
  'bg': '#f8f9fc',
  'bg-glow': '#eeedff',
  'surface': '#ffffff',
  'surface-2': '#f3f4f8',
  'line': '#b4b3c2',
  'line-soft': '#e6e5f0',
  'line-softer': '#f0eff6',
  'chip-line': '#d6d5e6',

  // Action colours
  'highlight': '#e4ea8f',
  'blue': '#cdeef6',
  'orange': '#ffc48f',
  'danger': '#c8321e',
  'danger-bg': '#ffe0db',
  'danger-hover-bg': '#ffe3dd',
  'focus': '#4f46e5',
  'on-ink': '#ffffff',

  // Pastel category palette
  'pastel-blue': '#cdeef6',
  'pastel-green': '#a8e6c1',
  'pastel-mint': '#d2f2e1',
  'pastel-yellow': '#fdf3b5',
  'pastel-orange': '#ffc48f',
  'pastel-pink': '#fbd5e8',
  'pastel-lilac': '#efdcfb',
  'pastel-periwinkle': '#e7e6ff',
  'pastel-coral': '#ffe0db',
  'pastel-peach': '#ffe2c7',
  'pastel-neutral': '#ecebf3',

  // Sidebar group colours
  'group-overview': '#cdeef6',
  'group-my-work': '#fdf3b5',
  'group-clients': '#a8e6c1',
  'group-automation': '#e7e6ff',
  'group-team': '#efdcfb',
  'group-family': '#fbd5e8',
  'group-home': '#d2f2e1',
  'group-money': '#ffc48f',

  // Status pills
  'status-success': '#d2f2e1',
  'status-pending': '#fdf3b5',
  'status-problem': '#ffe0db',
  'status-neutral': '#ecebf3',

  // Priority (decision 50)
  'priority-low': '#cdeef6',
  'priority-medium': '#ffc48f',
  'priority-high': '#ffb4a8',

  // Overlays
  'backdrop': 'rgba(30, 27, 58, 0.3)',
  'backdrop-drawer': 'rgba(30, 27, 58, 0.35)',

  // Typography
  'font-heading': "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  'font-body': "'Raleway', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  'text-h1': 'clamp(40px, 5vw, 60px)',
  'text-h1-line': '0.95',
  'text-h1-tracking': '-0.025em',
  'text-section': '20px',
  'text-card-title': '18px',
  'text-body': '16px',
  'text-secondary': '15px',
  'text-small': '14px',
  'text-eyebrow': '13px',
  'text-badge': '12px',
  'text-input': '16px',
  'text-button': '15px',
  'text-number-sm': '22px',
  'text-number': '28px',
  'text-number-lg': '40px',
  'text-topbar': '17px',
  'text-dcard-title': '20px',
  'safe-top': 'env(safe-area-inset-top, 0px)',
  'topbar-h': 'calc(var(--topbar-height) + var(--safe-top))',
  'shell-text': '16px',
  'shell-text-small': '14px',
  'chat-text': '17px',
  'chat-line': '1.55',
  'chat-history-title': '15px',
  'chat-history-meta': '13px',
  'tracking-eyebrow': '0.1em',

  // Radius
  'radius-card': '20px',
  'radius-inner': '16px',
  'radius-modal': '22px',
  'radius-popover': '14px',
  'radius-tabs': '14px',
  'radius-button': '12px',
  'radius-button-sm': '10px',
  'radius-input': '10px',
  'radius-tab': '10px',
  'radius-checkbox': '6px',
  'radius-empty': '18px',
  'radius-ask': '18px',
  'radius-pill': '999px',

  // Shadow
  'shadow-card': '0 14px 34px -18px rgba(30, 27, 58, 0.33)',
  'shadow-modal': '0 24px 60px -20px rgba(30, 27, 58, 0.45)',
  'shadow-popover': '0 16px 36px -14px rgba(30, 27, 58, 0.4)',

  // Borders
  'border-strong': '1.5px',
  'border': '1px',

  // Spacing
  'space-1': '4px',
  'space-1-5': '6px',
  'space-2': '8px',
  'space-2-5': '10px',
  'space-3': '12px',
  'space-3-5': '14px',
  'space-4': '16px',
  'space-5': '20px',
  'space-6': '24px',
  'space-8': '32px',
  'space-10': '40px',
  'space-14': '56px',

  // Layout
  'content-max': '1280px',
  'page-pad': '40px clamp(20px, 4vw, 56px) 80px',
  'grid-gap': '24px',
  'sidebar-width': '272px',
  'sidebar-rail': '72px',
  'topbar-height': '56px',
  'drawer-width': 'min(320px, 86vw)',
  'panel-width': 'min(420px, 100vw)',
  'touch-target': '44px',
  'ask-size': '58px',
  'ask-bottom': 'var(--space-8)',   // fresh-eyes item 20
  'ask-clearance': 'calc(var(--ask-bottom) + var(--ask-size) + 28px)',
  'lift': '-3px',

  // Layering
  'z-sticky': '10',
  'z-ask': '40',
  'z-shell': '50',
  'z-drawer': '60',
  'z-panel': '70',
  'z-modal': '90',
  'z-popover': '95',
  'z-toast': '100',
};

// What tokens.css redefines at phone width (up to 767px).
export const PHONE_TOKENS = {
  'text-input': '16px',
  'text-dcard-title': '18px',
  'text-h1': 'clamp(30px, 8vw, 40px)',
  'grid-gap': '16px',
  'page-pad': '20px 16px calc(116px + env(safe-area-inset-bottom))',
  'ask-size': '68px',
  'ask-bottom': 'calc(var(--space-5) + env(safe-area-inset-bottom, 0px))',
};

// The layering scale as numbers, for the rare inline zIndex.
export const Z = {
  sticky: 10, ask: 40, shell: 50, drawer: 60, panel: 70, modal: 90, popover: 95, toast: 100,
};

// 'var(--ink)' for a token name; throws on a name that is not a token, so a typo
// fails loudly in development and in tests instead of rendering nothing.
export function v(name) {
  if (!Object.prototype.hasOwnProperty.call(TOKENS, name)) {
    throw new Error(`Unknown design token: ${name}`);
  }
  return `var(--${name})`;
}
